// The Live page's journey map, as pure functions (owner, 2026-10-10).
//
// Columns = our real stages: Écrit · Photos · Album · Aperçu · Validé · Payé.
// Each client is a chip. Its colour is a fact, never a guess:
//   blue  — the agent acted for her in the last 2 minutes (or is writing now)
//   red   — stuck on US (the board's own rule: `stuck` and the ball is ours)
//   amber — waiting for YOUR decision (an open Inbox item)
// Each column: red first (oldest wait first), then amber, then recently moved.
//
// Guard: ./journey.test.ts
import type { InboxItem, JourneyTimes, PipelineClient } from '@/lib/api';

export const COLUMNS = ['ecrit', 'photos', 'album', 'apercu', 'valide', 'paye'] as const;
export type Column = typeof COLUMNS[number];
export const COLUMN_LABEL: Record<Column, string> = {
  ecrit: 'Écrit', photos: 'Photos', album: 'Album', apercu: 'Aperçu', valide: 'Validé', paye: 'Payé',
};

/** Mirrors worker/src/lib/clientStage.ts `journeyColumn`. */
export function columnOf(stage: string | null | undefined): Column | null {
  switch (stage) {
    case 'new_lead': case 'talking': return 'ecrit';
    case 'order_created': case 'cover_chosen': case 'photos_stored': return 'photos';
    case 'album_built': return 'album';
    case 'preview_sent': return 'apercu';
    case 'approved': return 'valide';
    case 'paid': case 'printing': case 'shipped': case 'delivered': return 'paye';
    default: return null;
  }
}

export type Tone = 'blue' | 'red' | 'amber' | 'plain';

export interface Chip {
  key: string;
  name: string;
  chatId: string | null;
  orderId: string | null;
  shopifyOrderId: string | null;
  orderName: string | null;
  column: Column;
  tone: Tone;
  /** What the timer counts from: the wait (red/amber), else when she reached this step. */
  sinceIso: string | null;
  /** Her open Inbox items (amber) — what ✓ / ✗ act on. */
  items: InboxItem[];
  client: PipelineClient;
}

const ms = (iso: string | null | undefined) => {
  const t = Date.parse(String(iso ?? ''));
  return Number.isFinite(t) ? t : NaN;
};
const DAY = 24 * 3_600_000;
/** A delivered book stays on « Payé » this long, then the journey is done. */
const DONE_STAYS_MS = 3 * DAY;
/** A lead's chip stays on « Écrit » while she wrote this recently. */
const LEAD_STAYS_MS = 14 * DAY;
/** The agent's action that lights a chip. */
export const ACTING_MS = 2 * 60_000;

export interface MapInput {
  clients: PipelineClient[];
  items: InboxItem[];
  /** chat id → when the agent last acted for her (sent, or writing now). */
  acting: Map<string, number>;
  nowMs: number;
}

/** Which clients are on the map now, in which column, in which colour. Pure. */
export function chipsNow(input: MapInput): Chip[] {
  const out: Chip[] = [];
  for (const c of input.clients) {
    if (!c.active || c.ignored) continue;
    const column = columnOf(c.stage);
    if (!column) continue;
    if (column === 'ecrit' && !(input.nowMs - ms(c.lastActivityIso) <= LEAD_STAYS_MS)) continue;
    if (c.stage === 'delivered' && !(input.nowMs - ms(c.sinceIso) <= DONE_STAYS_MS)) continue;
    const items = input.items.filter((i) => (i.orderId && i.orderId === c.primary?.id) || (i.chatId && c.chatIds.includes(i.chatId)));
    const actedAt = Math.max(-Infinity, ...c.chatIds.map((id) => input.acting.get(id) ?? -Infinity));
    let tone: Tone = 'plain';
    let sinceIso = c.journey?.[column] ?? c.sinceIso;
    if (input.nowMs - actedAt <= ACTING_MS) tone = 'blue';
    else if (c.stuck && c.ballWith === 'ours') { tone = 'red'; sinceIso = c.sinceIso; }
    else if (items.length) { tone = 'amber'; sinceIso = items.map((i) => i.sinceIso).sort()[0] ?? sinceIso; }
    out.push({
      key: c.key, name: c.name || 'Sans nom', chatId: c.chatId, orderId: c.primary?.id ?? null,
      shopifyOrderId: c.primary?.shopifyOrderId ?? null, orderName: c.primary?.name ?? null,
      column, tone, sinceIso, items, client: c,
    });
  }
  return out;
}

const RANK: Record<Tone, number> = { red: 0, amber: 1, blue: 2, plain: 2 };

/** One column's order: red (oldest wait first), amber, then the most recently moved. Pure. */
export function orderColumn(chips: Chip[]): Chip[] {
  return [...chips].sort((a, b) => {
    const r = RANK[a.tone] - RANK[b.tone];
    if (r) return r;
    if (a.tone === 'red' || a.tone === 'amber') return ms(a.sinceIso) - ms(b.sinceIso);
    return ms(b.sinceIso) - ms(a.sinceIso);
  });
}

/** « Écrit » holds 100+: the red and amber ones, then the last 10, then « +N ». Pure. */
export function visibleOf(chips: Chip[], recent = 10): { shown: Chip[]; more: number } {
  const ordered = orderColumn(chips);
  const flagged = ordered.filter((c) => c.tone === 'red' || c.tone === 'amber');
  const rest = ordered.filter((c) => c.tone !== 'red' && c.tone !== 'amber');
  const shown = [...flagged, ...rest.slice(0, recent)];
  return { shown, more: ordered.length - shown.length };
}

/** Where she was at an instant — the replay. Null = not on the map yet. Pure. */
export function columnAt(j: JourneyTimes | undefined, atMs: number): Column | null {
  if (!j) return null;
  let col: Column | null = null;
  for (const c of COLUMNS) {
    const t = ms(j[c]);
    if (Number.isFinite(t) && t <= atMs) col = c;
  }
  return col;
}

/** The map at an instant: every client who had written within 14 days of it. Pure. */
export function chipsAt(clients: PipelineClient[], atMs: number): Chip[] {
  const out: Chip[] = [];
  for (const c of clients) {
    const column = columnAt(c.journey, atMs);
    if (!column) continue;
    const reached = ms(c.journey?.[column]);
    if (column === 'ecrit' && atMs - reached > LEAD_STAYS_MS) continue;
    if (column === 'paye' && atMs - reached > DONE_STAYS_MS) continue;
    out.push({
      key: c.key, name: c.name || 'Sans nom', chatId: c.chatId, orderId: c.primary?.id ?? null,
      shopifyOrderId: c.primary?.shopifyOrderId ?? null, orderName: c.primary?.name ?? null,
      column, tone: 'plain', sinceIso: c.journey?.[column] ?? null, items: [], client: c,
    });
  }
  return out;
}

/** « 3 h », « 14 j », « 6 min » — a wait, short. Pure. */
export function waitShort(sinceIso: string | null | undefined, nowMs: number): string {
  const t = ms(sinceIso);
  if (!Number.isFinite(t)) return '';
  const min = Math.max(0, Math.floor((nowMs - t) / 60_000));
  if (min < 1) return 'maint.';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} j`;
}

/** The three numbers. Pure. */
export function threeNumbers(chips: Chip[], items: InboxItem[], handledAlone: number | undefined) {
  const red = chips.filter((c) => c.tone === 'red');
  const needKeys = new Set<string>();
  for (const i of items) needKeys.add(i.orderId ?? i.chatId ?? i.key);
  return { handledAlone: handledAlone ?? 0, waitingOnUs: red.length, needsYou: needKeys.size, red };
}
